import Cookies from 'js-cookie';
import type { PostHog, PostHogConfig } from 'posthog-js';

import { COOKIE } from '@/components/layout/utils';

type Schedule = (run: () => void) => () => void;
type Command = { run: (client: PostHog) => void; requiresConsent: boolean };
const MAX_PENDING_COMMANDS = 200;

function scheduleWhenIdle(run: () => void): () => void {
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(run, { timeout: 1500 });
    return () => window.cancelIdleCallback(id);
  }
  const id = window.setTimeout(run, 0);
  return () => window.clearTimeout(id);
}

const enabled = () =>
  typeof window !== 'undefined' &&
  Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY) &&
  (process.env.NEXT_PUBLIC_NODE_ENV !== 'development' || process.env.NEXT_PUBLIC_POSTHOG_DEV_TRACKING === 'true');
const hasConsent = () => Cookies.get(COOKIE.posthogIdentifyKey) === 'true';

/** Keep analytics off the interactive startup path without reordering identity and event calls. */
export function createDeferredAnalytics(loadSdk: () => Promise<{ default: PostHog }>, schedule: Schedule = scheduleWhenIdle) {
  let client: PostHog | undefined;
  let pending: Command[] = [];
  let configuration: { key: string; options: Partial<PostHogConfig> } | undefined;
  let cancelScheduled: (() => void) | undefined;
  let loading = false;
  let active = false;
  let generation = 0;

  const run = (command: Command, sdk: PostHog) => {
    // Consent may have changed while the SDK was downloading.
    if (command.requiresConsent && !hasConsent()) return;
    try {
      command.run(sdk);
    } catch {
      // Analytics must never interrupt a wallet operation or app rendering.
    }
  };

  const scheduleLoad = () => {
    if (!active || client || loading || cancelScheduled || !configuration) return;
    cancelScheduled = schedule(() => {
      cancelScheduled = undefined;
      const current = generation;
      const config = configuration;
      if (!active || !config) return;
      loading = true;
      void loadSdk()
        .then(({ default: sdk }) => {
          if (!active || current !== generation) return;
          sdk.init(config.key, config.options);
          client = sdk;
          const commands = pending;
          pending = [];
          for (const command of commands) run(command, sdk);
        })
        .catch(() => {
          // A later event can retry a failed download; never reject into the app.
        })
        .finally(() => {
          loading = false;
          if (active && current !== generation) scheduleLoad();
        });
    });
  };

  const enqueue = (command: Command) => {
    if (!enabled() || (command.requiresConsent && !hasConsent())) return;
    if (client) {
      run(command, client);
      return;
    }
    if (pending.length >= MAX_PENDING_COMMANDS) {
      // Bound memory during a long outage. Retire the abandoned identity history
      // too, so retained events cannot be assigned to an older wallet.
      pending = [{ run: (sdk) => sdk.reset(), requiresConsent: false }];
    }
    pending.push(command);
    scheduleLoad();
  };

  return {
    init(key: string, options: Partial<PostHogConfig>) {
      if (!enabled()) return () => {};
      configuration = { key, options };
      active = true;
      const current = ++generation;
      scheduleLoad();
      return () => {
        if (current !== generation) return;
        active = false;
        ++generation;
        cancelScheduled?.();
        cancelScheduled = undefined;
        pending = [];
      };
    },
    capture(...args: Parameters<PostHog['capture']>) {
      enqueue({
        run: (sdk) => {
          sdk.capture(...args);
        },
        requiresConsent: false,
      });
    },
    identify(...args: Parameters<PostHog['identify']>) {
      enqueue({ run: (sdk) => sdk.identify(...args), requiresConsent: true });
    },
    reset(...args: Parameters<PostHog['reset']>) {
      enqueue({ run: (sdk) => sdk.reset(...args), requiresConsent: false });
    },
    setPersonProperties(...args: Parameters<PostHog['setPersonProperties']>) {
      enqueue({ run: (sdk) => sdk.setPersonProperties(...args), requiresConsent: false });
    },
    captureException(...args: Parameters<PostHog['captureException']>) {
      enqueue({
        run: (sdk) => {
          sdk.captureException(...args);
        },
        requiresConsent: true,
      });
    },
    get __loaded() {
      return Boolean(client);
    },
    has_opted_in_capturing() {
      return client?.has_opted_in_capturing() ?? false;
    },
  };
}

const posthog = createDeferredAnalytics(() => import('posthog-js'));
export default posthog;
